/**
 * @file src/input/midi/MidiMappingPersistence.js
 * @description Bridges the runtime mapping store with where mappings are kept.
 *
 * "Where they are kept" is `midiSliceSource`, not the network directly: a slice is served from the
 * server when it answers and from this browser's mirror when it does not, and a save is recorded
 * locally once the server has taken it. This module's job is unchanged — resolve a widget's slice,
 * put the bindings into `ScopedMidiMap`, and write a learned one back — it simply no longer assumes
 * a reachable server is the only place an answer can come from.
 */
import {
  READ_OUTCOME,
  VALUE_SOURCE,
  configurationEpoch,
  isConfigurationEpochCurrent,
} from '../../config/configurationPack.js';
import { loadMidiSlices, saveMidiBinding, clearMidiBinding } from './midiSliceSource.js';
import { makeScopeKey } from './scopeKey.js';
import { qualifyForSlot } from './slotQualifier.js';

export class MidiMappingPersistence {
  constructor({ scopedMidiMap, resolveScope, resolveParameterId, resolveDeviceInfo }) {
    // Persisted bindings live PER SLICE in `ScopedMidiMap` (keyed by scopeKey), not in one
    // flat registry map. Save/clear write into the relevant slice — the scope/entityId pair
    // is the binding's own persistence identity, resolved at learn time.
    this.scopedMidiMap = scopedMidiMap;
    this.resolveScope = resolveScope;
    this.resolveParameterId = resolveParameterId;
    this.resolveDeviceInfo = resolveDeviceInfo;
  }

  /**
   * Load the persisted MIDI mappings for the present slices into `ScopedMidiMap`.
   *
   * How few requests that takes is `midiSliceSource`'s business (one scoped request for a single
   * orbiter, one tree pull split across several, one per other scope, and none at all for a slice
   * this page already resolved). Every slice load is EPOCH-GUARDED: the epoch is snapshotted before
   * the load starts, and an answer that raced a local learn/unmap is discarded — the local write
   * wins, and its own save delivers it to the server. The load is guarded against a change of
   * PERSON the same way, and for the same reason: an answer resolved for whoever was here before
   * must not be applied to the runtime the new person is looking at.
   * @param {string[]} scopeKeys the slices currently live (see scopeKey.js).
   */
  async loadMany(scopeKeys) {
    const keys = (scopeKeys || []).filter(Boolean);
    if (!keys.length) {
      return;
    }

    // Whose mappings these are, as of now. A load that straddles a change of identity has already
    // resolved its slices for the person who left — from this page's memo, or from their rows on
    // disk — and applying them here would put their bindings back into a runtime that was just
    // cleared of them. The identity edge starts a load of its own, so returning quietly is the
    // whole job.
    const identityAtStart = configurationEpoch();
    const epochs = new Map(keys.map((key) => [key, this.scopedMidiMap.epoch(key)]));
    // `midiSliceSource` decides where each slice comes from — the server, or this browser's mirror
    // when the server cannot be reached. A slice it could not resolve at all is left NOT LOADED
    // rather than loaded-and-empty: those two look identical to a widget, and only the second one
    // tells somebody their controller was never mapped.
    const slices = await loadMidiSlices(keys);
    if (!isConfigurationEpochCurrent(identityAtStart)) return;

    let resolvedCount = 0;
    for (const key of keys) {
      const resolvedSlice = slices.get(key);
      if (!resolvedSlice || resolvedSlice.source === VALUE_SOURCE.NONE) continue;
      resolvedCount += 1;
      // The epoch guard is unchanged and still belongs here: a response — from either side — that
      // raced a local learn is discarded, and the local write wins. Counted as resolved all the
      // same, because it WAS resolved; it just lost to something newer.
      this.scopedMidiMap.loadSlice(key, resolvedSlice.slice, epochs.get(key));
    }

    // Nothing could be resolved, from either side. Reported the way it always was, so the caller
    // still tells an auth failure from an outage and shows the login prompt or logs it.
    if (!resolvedCount) {
      const outcome = slices.get(keys[0])?.outcome;
      throw new Error(
        outcome === READ_OUTCOME.UNAUTHORIZED
          ? 'Authentication required to load MIDI mappings'
          : 'MIDI mappings are unavailable, and none are stored on this device.',
      );
    }
  }

  async saveBinding({ element, identifier, context, midiPayload }) {
    const scopeInfo = this.resolveScope(element, identifier);
    if (!scopeInfo?.scope || !scopeInfo.entityId) {
      throw new Error('Unable to resolve scope for MIDI mapping.');
    }
    const parameterId = this.resolveParameterId(element, identifier);
    if (!parameterId) {
      throw new Error('Unable to resolve parameter id for MIDI mapping.');
    }
    const deviceInfo = this.resolveDeviceInfo(midiPayload.event);
    // A collection-owned binding is STORED under its stage (`slot-2::layered:…`) so two stages
    // keep independent mappings inside the one collection slice. Every other binding stores its
    // bare runtime id → unchanged on the wire.
    const storedParameterId = qualifyForSlot(parameterId, scopeInfo.slot);
    // Whose gesture this is, as of now — the same rule the load follows, for the same reason. A
    // save that straddles a change of identity has already been refused everywhere it would have
    // been written down; what was left was this continuation, which put the previous person's CC
    // route straight back into the runtime the arriving one is looking at, moments after the
    // identity edge had cleared it.
    const identityAtStart = configurationEpoch();
    await saveMidiBinding({
      scope: scopeInfo.scope,
      entityId: scopeInfo.entityId,
      parameterId: storedParameterId,
      binding: {
        deviceId: deviceInfo.deviceId,
        channel: midiPayload.channel + 1,
        cc: midiPayload.cc,
      },
    });
    if (!isConfigurationEpochCurrent(identityAtStart)) return;
    // Post-save reconciliation only — the gesture-time write in MIDIController already put
    // this binding in the store (and advanced the epoch) BEFORE the network round-trip.
    this.scopedMidiMap.setBinding(
      makeScopeKey(scopeInfo.scope, scopeInfo.entityId),
      storedParameterId,
      { channel: midiPayload.channel, cc: midiPayload.cc },
    );
  }

  async clearBinding({ identifier, parameterKey, element }) {
    const parameterId = parameterKey || this.resolveParameterId(element, identifier);
    if (!parameterId) {
      throw new Error('Unable to resolve parameter id for MIDI mapping removal.');
    }
    const scopeInfo = this.resolveScope(element, identifier);
    if (!scopeInfo?.scope || !scopeInfo.entityId) {
      throw new Error('Unable to resolve scope for MIDI mapping removal.');
    }

    const storedParameterId = qualifyForSlot(parameterId, scopeInfo.slot);
    // Guarded like the save: a clear that outlived the person who asked for it would take a
    // binding the ARRIVING person had just loaded out of their runtime.
    const identityAtStart = configurationEpoch();
    await clearMidiBinding({
      scope: scopeInfo.scope,
      entityId: scopeInfo.entityId,
      parameterId: storedParameterId,
    });
    if (!isConfigurationEpochCurrent(identityAtStart)) return;
    this.scopedMidiMap.deleteBinding(
      makeScopeKey(scopeInfo.scope, scopeInfo.entityId),
      storedParameterId,
    );
  }
}
